import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import ApiConfig from "../../../../common/config/ApiConfig";
import { useLanguage } from "../../../../common/i18n/LanguageContext";

// No hardcoded stake lists here — every stake this form knows about comes
// from getLottoRewardTypeGroups, same as Add Agent Step1. An empty object is
// a perfectly valid starting point since rendering always falls back to
// `value[rt.code] ?? 0`.
export const emptyThaiCommission = () => ({});
export const emptySixFieldCommission = () => ({});

const LOTTO_TYPES = {
  thCommission: "th_gov",
  hanoiCommission: "hanoy_PhuQua",
  laosCommission: "laos_gov",
  yeekee5Commission: "Yeekee_5",
  yeekee10Commission: "Yeekee_10",
  yeekee15Commission: "Yeekee_15",
};

// Maps LottoType.type (the raw string API calls use, e.g. "th_gov") to the
// short filter key the lotto-type dropdown below uses.
const LOTTO_TYPE_KEY_BY_TYPE = {
  th_gov: "thai",
  hanoy_PhuQua: "hanoi",
  laos_gov: "laos",
  Yeekee_5: "yeekee",
  Yeekee_10: "yeekeevip",
  Yeekee_15: "yeekeevvip",
};

// Inverse of the above — the raw lotto_type for whichever filter key is
// currently selected, needed before GROUPS (which also carries this same
// mapping per-group) is computed further down.
const LOTTO_TYPE_RAW_BY_KEY = Object.fromEntries(
  Object.entries(LOTTO_TYPE_KEY_BY_TYPE).map(([raw, key]) => [key, raw]),
);

// rows are MemberCommission's stake-pivoted rows (one per { lotto_type,
// stake, commission }), same shape as DownlineCommission — e.g. the
// `memberCommissions` relation on a Member entity. The stake set comes
// straight from whatever rows exist, no predefined list needed.
export const commissionTablesFromRows = (rows = []) => {
  const pickFields = (lottoType) =>
    (rows || [])
      .filter((r) => r.lotto_type === lottoType)
      .reduce((acc, r) => ({ ...acc, [r.stake]: Number(r.commission) }), {});
  return {
    thCommission: pickFields(LOTTO_TYPES.thCommission),
    hanoiCommission: pickFields(LOTTO_TYPES.hanoiCommission),
    laosCommission: pickFields(LOTTO_TYPES.laosCommission),
    yeekee5Commission: pickFields(LOTTO_TYPES.yeekee5Commission),
    yeekee10Commission: pickFields(LOTTO_TYPES.yeekee10Commission),
    yeekee15Commission: pickFields(LOTTO_TYPES.yeekee15Commission),
  };
};

export const handleChangeCommission = (e, obj, setObj) => {
  let value = Number(e.target.value);
  if (isNaN(value) || value < 0) value = 0;
  if (value > 100) value = 100;
  setObj({ ...obj, [e.target.name]: value });
};

function CommissionTable({
  title,
  bgClass,
  rewardTypeGroups,
  value,
  setValue,
  uplineValue = {},
  disabled = false,
}) {
  const { t } = useLanguage();
  // Groups where "ใช้ค่าเดียวกันทั้งกลุ่ม" is checked — every stake's
  // commission in that group mirrors the group's first stake (each still
  // individually capped to its own upline value). While checked, only the
  // first stake's input stays editable; editing it cascades the value to
  // the rest of the group live.
  const [syncedGroupIds, setSyncedGroupIds] = useState({});

  // Commission entered for a code can never exceed the upline's own value
  // for that code (and never exceeds 100% regardless) — matches Step1 of
  // Add Agent.
  const clampCommission = (code, raw) => {
    const cap = uplineValue[code];
    const effectiveCap = cap !== undefined ? Math.min(cap, 100) : 100;
    return Math.min(Math.max(raw, 0), effectiveCap);
  };

  const cascadeToGroup = (group, rawValue) => {
    const rewardTypes = group.rewardTypes || [];
    setValue((prev) => {
      const next = { ...prev };
      rewardTypes.forEach((rt) => {
        next[rt.code] = clampCommission(rt.code, rawValue);
      });
      return next;
    });
  };

  const handleToggleSync = (group, checked) => {
    setSyncedGroupIds((prev) => ({ ...prev, [group.id]: checked }));
    if (!checked) return;
    const firstRt = (group.rewardTypes || [])[0];
    if (!firstRt) return;
    cascadeToGroup(group, value[firstRt.code] ?? 0);
  };

  const handleChange = (e, group, rt) => {
    const raw = Number(e.target.value);
    const cap = uplineValue[rt.code];
    const effectiveCap = cap !== undefined ? Math.min(cap, 100) : 100;
    if (raw > effectiveCap) {
      Swal.fire({
        title: t("member_commission_validate_title"),
        text: `${t("member_commission_exceed_prefix")} ${effectiveCap}%`,
        icon: "error",
      });
    }
    const isFirst = (group.rewardTypes || [])[0]?.id === rt.id;
    if (syncedGroupIds[group.id] && isFirst) {
      cascadeToGroup(group, raw);
      return;
    }
    setValue({ ...value, [rt.code]: clampCommission(rt.code, raw) });
  };

  return (
    <div className="w-full text-center justify p-1 rounded-sm bg-slate-200">
      <div className={`w-full text-center p-1 rounded-sm ${bgClass}`}>
        <span className="text-white font-bold">{title}</span>
      </div>
      {rewardTypeGroups.length === 0 && (
        <div className="bg-white p-4 rounded-lg shadow-sm text-center text-slate-500 text-[12px]">
          {t("member_commission_no_groups")}
        </div>
      )}
      {rewardTypeGroups.map((group) => {
        const firstRtId = (group.rewardTypes || [])[0]?.id;
        const synced = !!syncedGroupIds[group.id];
        return (
          <div
            key={group.id}
            className="rounded overflow-hidden mt-1 bg-white p-2"
          >
            <div className="flex items-center gap-2 mb-1">
              <label className="flex items-center gap-1 text-[11px] font-bold text-slate-600">
                <input
                  type="checkbox"
                  checked={synced}
                  disabled={disabled}
                  onChange={(e) => handleToggleSync(group, e.target.checked)}
                />
                {t("member_commission_sync_group")}
              </label>
              <span className="text-[11px] font-bold text-slate-600">
                {group.name}
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
              {(group.rewardTypes || []).map((rt) => {
                const isFirst = rt.id === firstRtId;
                const fieldDisabled = disabled || (synced && !isFirst);
                return (
                  <div
                    key={rt.id}
                    className="border-[1px] border-slate-300 rounded-lg p-2"
                    title={`${rt.code} (${rt.stake}) — ${rt.exp}`}
                  >
                    <div className="font-bold text-[12px] text-center mb-1">
                      {rt.name}
                    </div>
                    <div className="flex items-center gap-1">
                      <label className="text-[11px] text-slate-500 w-[70px] text-left shrink-0">
                        {t("member_commission_label")}
                      </label>
                      <div className="flex flex-col w-full">
                        {uplineValue[rt.code] !== undefined && (
                          <span className="font-bold text-amber-500 justify-items-center items-center text-[11px] text-center">
                            {uplineValue[rt.code]}%
                          </span>
                        )}
                        <input
                          type="number"
                          min="0"
                          max={Math.min(uplineValue[rt.code] ?? 100, 100)}
                          step="0.01"
                          disabled={fieldDisabled}
                          name={rt.code}
                          value={value[rt.code] ?? 0}
                          onChange={(e) => handleChange(e, group, rt)}
                          placeholder="0"
                          className="w-full font-bold text-[12px] border-[1px] border-slate-300 rounded-lg text-center disabled:bg-slate-100 disabled:text-slate-400"
                          onClick={(e) => e.target.select()}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function MemberCommissionTables({
  thCommission,
  setThCommission,
  hanoiCommission,
  setHanoiCommission,
  laosCommission,
  setLaosCommission,
  yeekee5Commission,
  setYeekee5Commission,
  yeekee10Commission,
  setYeekee10Commission,
  yeekee15Commission,
  setYeekee15Commission,
  // Package selector, rendered next to the lotto-type filter below. A member
  // picks its own package PER lotto_type (unlike an agent's `packages`,
  // which are just toggled on/off) — packageIds is keyed by the raw
  // lotto_type string (LOTTO_TYPES values above), one entry per type, so
  // switching the "กรองตามหวย" filter shows/edits that type's own
  // selection. Only Member.jsx/Membertable.jsx pass these; leave
  // setPackageIds unset for callers that don't need this (none currently —
  // this component is only used by those two).
  packages,
  packageIds,
  setPackageIds,
  // Whose packages/upline-commission this is — required whenever
  // setPackageIds is passed, since the per-lotto_type upline-cap fetch below
  // needs to know which agent to ask.
  agentUsername,
}) {
  const { t } = useLanguage();
  const [selectedLotto, setSelectedLotto] = useState("thai");
  const [lottoTypes, setLottoTypes] = useState([]);
  const [rewardTypeGroups, setRewardTypeGroups] = useState([]);

  useEffect(() => {
    ApiConfig.get("/getLottoTypes", {}).then((result) => {
      if (result.resCode !== 200) {
        toast.error(result.message || t("common_load_error"), {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
      setLottoTypes(Array.isArray(result.data) ? result.data : []);
    });
    ApiConfig.get("/getLottoRewardTypeGroups").then((result) => {
      if (result.resCode !== 200) {
        toast.error(result.message || t("common_load_error"), {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
      setRewardTypeGroups(Array.isArray(result.data) ? result.data : []);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Once the reward-type groups are known, every commission field defaults
  // to 0 in state (not just at render time) — any stake the user hasn't
  // touched, or that didn't have a saved row yet, still submits as an
  // explicit 0 rather than being silently absent from the payload.
  useEffect(() => {
    if (rewardTypeGroups.length === 0) return;
    const fillDefaults = (lottoTypeRaw, setValue) => {
      const codes = rewardTypeGroups
        .filter((g) => g.lottoType?.type === lottoTypeRaw)
        .flatMap((g) => (g.rewardTypes || []).map((rt) => rt.code));
      setValue((prev) => {
        const next = { ...prev };
        let changed = false;
        codes.forEach((code) => {
          if (!(code in next)) {
            next[code] = 0;
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    };
    fillDefaults(LOTTO_TYPES.thCommission, setThCommission);
    fillDefaults(LOTTO_TYPES.hanoiCommission, setHanoiCommission);
    fillDefaults(LOTTO_TYPES.laosCommission, setLaosCommission);
    fillDefaults(LOTTO_TYPES.yeekee5Commission, setYeekee5Commission);
    fillDefaults(LOTTO_TYPES.yeekee10Commission, setYeekee10Commission);
    fillDefaults(LOTTO_TYPES.yeekee15Commission, setYeekee15Commission);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rewardTypeGroups]);

  // Upline-commission cap, fetched per lotto_type (each type can be under a
  // different package) rather than once for the whole member — re-fetched
  // whenever the active tab's own package selection changes.
  const [uplineCommissionRows, setUplineCommissionRows] = useState([]);
  const activePackageId = packageIds?.[LOTTO_TYPE_RAW_BY_KEY[selectedLotto]];
  useEffect(() => {
    if (!setPackageIds) return;
    if (!agentUsername || !activePackageId) {
      setUplineCommissionRows([]);
      return;
    }
    const params = new URLSearchParams({
      agent: agentUsername,
      package_id: activePackageId,
    });
    ApiConfig.get(`/getfightpercentandcommission?${params.toString()}`).then(
      (result) => {
        if (result.resCode !== 200) return;
        const list = Array.isArray(result.data) ? result.data : [];
        const commissionArray = list.filter((obj) => "commission" in obj);
        const cmRows = Array.isArray(commissionArray[0]?.commission)
          ? commissionArray[0].commission
          : [];
        setUplineCommissionRows(cmRows);
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentUsername, activePackageId]);

  const buildUplineForLotto = (lottoTypeRaw) => {
    const codes = rewardTypeGroups
      .filter((g) => g.lottoType?.type === lottoTypeRaw)
      .flatMap((g) => (g.rewardTypes || []).map((rt) => rt.code));
    return codes.reduce((acc, code) => {
      const row = uplineCommissionRows.find(
        (r) => r.lotto_type === lottoTypeRaw && r.stake === code,
      );
      return { ...acc, [code]: row ? Number(row.commission) : undefined };
    }, {});
  };

  const GROUPS = {
    thai: {
      title: t("member_commission_group_thai"),
      bgClass: "bg-sky-500",
      lottoTypeRaw: LOTTO_TYPES.thCommission,
      value: thCommission,
      setValue: setThCommission,
    },
    hanoi: {
      title: t("member_commission_group_hanoi"),
      bgClass: "bg-red-500",
      lottoTypeRaw: LOTTO_TYPES.hanoiCommission,
      value: hanoiCommission,
      setValue: setHanoiCommission,
    },
    laos: {
      title: t("member_commission_group_laos"),
      bgClass: "bg-blue-800",
      lottoTypeRaw: LOTTO_TYPES.laosCommission,
      value: laosCommission,
      setValue: setLaosCommission,
    },
    yeekee: {
      title: t("member_commission_group_yeekee"),
      bgClass: "bg-orange-400",
      lottoTypeRaw: LOTTO_TYPES.yeekee5Commission,
      value: yeekee5Commission,
      setValue: setYeekee5Commission,
    },
    yeekeevip: {
      title: t("member_commission_group_yeekeevip"),
      bgClass: "bg-rose-700",
      lottoTypeRaw: LOTTO_TYPES.yeekee10Commission,
      value: yeekee10Commission,
      setValue: setYeekee10Commission,
    },
    yeekeevvip: {
      title: t("member_commission_group_yeekeevvip"),
      bgClass: "bg-rose-900",
      lottoTypeRaw: LOTTO_TYPES.yeekee15Commission,
      value: yeekee15Commission,
      setValue: setYeekee15Commission,
    },
  };

  const active = GROUPS[selectedLotto] ?? GROUPS.thai;
  const activeRewardTypeGroups = rewardTypeGroups.filter(
    (g) => LOTTO_TYPE_KEY_BY_TYPE[g.lottoType?.type] === selectedLotto,
  );

  return (
    <div className="w-full flex flex-col space-y-2 mt-2">
      <div className="flex items-center gap-2">
        <label className="text-[13px] font-bold">
          {t("member_commission_filter_label")}
        </label>
        <select
          value={selectedLotto}
          onChange={(e) => setSelectedLotto(e.target.value)}
          className="border-[1px] border-slate-300 rounded-lg text-[13px] font-bold px-2 py-1"
        >
          {lottoTypes
            .filter((lt) => LOTTO_TYPE_KEY_BY_TYPE[lt.type])
            .map((lt) => (
              <option key={lt.id} value={LOTTO_TYPE_KEY_BY_TYPE[lt.type]}>
                {lt.name}
              </option>
            ))}
        </select>
        {setPackageIds && (
          <>
            <label className="text-[13px] font-bold">
              {t("member_package_label")}
            </label>
            <select
              value={packageIds?.[active.lottoTypeRaw] ?? ""}
              onChange={(e) =>
                setPackageIds((prev) => ({
                  ...prev,
                  [active.lottoTypeRaw]: e.target.value,
                }))
              }
              className="border-[1px] border-slate-300 rounded-lg text-[13px] font-bold px-2 py-1"
            >
              <option value="">{t("member_package_placeholder")}</option>
              {(packages ?? []).map((pkg) => (
                <option key={pkg.id} value={String(pkg.id)}>
                  {t(pkg.code) !== pkg.code ? t(pkg.code) : pkg.name}
                </option>
              ))}
            </select>
          </>
        )}
      </div>
      <CommissionTable
        title={active.title}
        bgClass={active.bgClass}
        rewardTypeGroups={activeRewardTypeGroups}
        value={active.value}
        setValue={active.setValue}
        uplineValue={buildUplineForLotto(active.lottoTypeRaw)}
        // A member must belong to a package (for THIS lotto_type) before its
        // commission can be set — only enforced when the package selector
        // above is actually shown (setPackageIds passed).
        disabled={!!setPackageIds && !packageIds?.[active.lottoTypeRaw]}
      />
    </div>
  );
}
